// PerOPDSupportTrackingPage.js
import React, { useEffect, useState, useMemo } from "react";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import { Search, Filter, Download, X, ChevronDown, ChevronUp, ChevronsUpDown, Plus } from 'lucide-react';

export default function PerOPDSupportTrackingPage() {
  const [members, setMembers] = useState([]);
  const [opdsSource, setOpdsSource] = useState([]); // backend OPD list
  const [opdSupports, setOpdSupports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('tracking'); // 'tracking' | 'provided'
  const [filters, setFilters] = useState({
    search: "",
    opd: "",
    support: "",
    status: "",
    organization: "",
  });
  const [selectedSupport, setSelectedSupport] = useState({});
  const [supportDate, setSupportDate] = useState({});
  const [supportAmount, setSupportAmount] = useState({});
  const [supportDescription, setSupportDescription] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: "opdName", direction: "asc" });
  const itemsPerPage = 10;

  const SUPPORT_CATEGORIES = [
    "Materials",
    "Training",
    "Job",
    "Education",
    "Healthcare",
    "Financial Support",
    "Holiday & Welfare Support",
    "Other",
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api';

      // 1) Load all OPDs from backend
      const opdRes = await fetch(`${API_BASE_URL}/get_opds.php`);
      const opdJson = await opdRes.json();
      const opdList = opdJson?.status === 'success' && Array.isArray(opdJson.data) ? opdJson.data : [];
      setOpdsSource(opdList);

      // Map contact_email -> OPD name for quick lookup
      const emailToOpdName = new Map(
        opdList
          .filter(o => o.contact_email && o.name)
          .map(o => [o.contact_email.toLowerCase(), o.name])
      );

      // 2) Load all members from backend
      const membersRes = await fetch(`${API_BASE_URL}/getAllMembers.php`);
      const membersJson = await membersRes.json();
      const allMembers = Array.isArray(membersJson) ? membersJson : [];

      const formattedMembers = allMembers.map((m) => {
        const supportReceived = Array.isArray(m.supportReceived)
          ? m.supportReceived
          : m.supportReceived
          ? m.supportReceived.split(",").map((s) => s.trim()).filter(Boolean)
          : [];
        const supportNeeded = (m.supportNeeded || '')
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);

        // Derive OPD name from OPD list using opdEmail/contact_email mapping
        const opdEmail = (m.opdEmail || m.opd_email || '').toLowerCase();
        const opdName = emailToOpdName.get(opdEmail) || m.opdName || 'Unknown OPD';

        return {
          ...m,
          opdName,
          supportReceived,
          supportNeeded,
        };
      });

      setMembers(formattedMembers);

      // 3) Load per-OPD supports from backend
      const supportsRes = await fetch(`${API_BASE_URL}/get_opd_supports.php`);
      const supportsJson = await supportsRes.json();
      const supportsList =
        supportsJson?.status === 'success' && Array.isArray(supportsJson.data)
          ? supportsJson.data
          : [];
      setOpdSupports(supportsList);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    }
    setLoading(false);
  };

  const statusColor = (status) => {
    if (status === "Completed") return "bg-green-500";
    if (status === "In Progress") return "bg-orange-500";
    return "bg-red-500";
  };

  // Group members into OPDs based on full OPD list so all OPDs appear
  const opds = useMemo(() => {
    if (!Array.isArray(opdsSource) || opdsSource.length === 0) return [];

    return opdsSource.map((opd) => {
      const opdEmail = (opd.contact_email || '').toLowerCase();
      const membersForOpd = members.filter((m) => {
        const mEmail = (m.opdEmail || m.opd_email || '').toLowerCase();
        return opdEmail && mEmail && mEmail === opdEmail;
      });

      return {
        opdName: opd.name || 'Unknown OPD',
        members: membersForOpd,
      };
    });
  }, [opdsSource, members]);

  // Aggregate supports
  const aggregateSupports = (opd) => {
    const counts = {};
    // Build needs map from members' supportNeeded
    opd.members.forEach((m) => {
      m.supportNeeded.forEach((s) => {
        counts[s] = (counts[s] || 0) + 1;
      });
    });

    // Deduct given supports ONLY for types that exist in the needs map
    opdSupports
      .filter((s) => s.opdName === opd.opdName)
      .forEach((s) => {
        if (Object.prototype.hasOwnProperty.call(counts, s.supportType)) {
          counts[s.supportType] = (counts[s.supportType] || 0) - s.amount;
          if (counts[s.supportType] < 0) counts[s.supportType] = 0;
        }
      });

    return counts;
  };

  const handleAddSupport = async (opdName) => {
    const support = selectedSupport[opdName];
    const dateGiven = supportDate[opdName];
    const amount = parseInt(supportAmount[opdName], 10);
    const description = (supportDescription[opdName] || '').trim();

    if (!support || !description || !dateGiven || !amount) {
      alert("Please select support, write description, pick date, and enter amount");
      return;
    }

    const supportCounts = aggregateSupports({
      opdName,
      members: members.filter((m) => m.opdName === opdName),
    });
    const hasDefinedNeed = Object.prototype.hasOwnProperty.call(supportCounts, support);
    const remaining = hasDefinedNeed ? supportCounts[support] || 0 : null;

    if (hasDefinedNeed && amount > remaining) {
      alert(`You can only provide up to ${remaining} for ${support}`);
      return;
    }

    try {
      const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api';
      await fetch(`${API_BASE_URL}/add_opd_support.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          opdName,
          supportType: support,
          organization: "Association",
          amount,
          dateGiven,
          notes: description,
        }),
      });

      await fetchData();

      setSelectedSupport((prev) => ({ ...prev, [opdName]: "" }));
      setSupportDate((prev) => ({ ...prev, [opdName]: "" }));
      setSupportAmount((prev) => ({ ...prev, [opdName]: "" }));
      setSupportDescription((prev) => ({ ...prev, [opdName]: "" }));
    } catch (error) {
      console.error("Failed to add support:", error);
    }
  };

  const handleRemoveSupport = async (entryId) => {
    try {
      const API_BASE_URL = 'https://aaapd.org.et/gallery_backend/api';
      await fetch(`${API_BASE_URL}/delete_opd_support.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: entryId }),
      });
      await fetchData();
    } catch (error) {
      console.error("Failed to remove support:", error);
    }
  };

  const onFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setCurrentPage(1);
  };

  // Enhanced filtering
  const filteredOpds = useMemo(() => {
    return opds.filter((opd) => {
      const matchesSearch = opd.opdName.toLowerCase().includes(filters.search.toLowerCase());
      const matchesOpd = filters.opd ? opd.opdName === filters.opd : true;

      const supportCounts = aggregateSupports(opd);
      const matchesSupport = filters.support
        ? Object.keys(supportCounts).includes(filters.support)
        : true;

      // Status filtering
      const receivedSupportsForOpd = opdSupports.filter((s) => s.opdName === opd.opdName);
      const allSupportsForOpd = Object.keys(supportCounts);
      const status = allSupportsForOpd.every((s) => supportCounts[s] === 0)
        ? "Completed"
        : receivedSupportsForOpd.length > 0
        ? "In Progress"
        : "Pending";
      
      const matchesStatus = filters.status ? status === filters.status : true;

      return matchesSearch && matchesOpd && matchesSupport && matchesStatus;
    });
  }, [opds, filters, opdSupports]);

  // Sorting
  const sortedOpds = useMemo(() => {
    const sorted = [...filteredOpds];
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        let aVal, bVal;
        
        if (sortConfig.key === "opdName") {
          aVal = a.opdName;
          bVal = b.opdName;
        } else if (sortConfig.key === "totalSupports") {
          const aCounts = aggregateSupports(a);
          const bCounts = aggregateSupports(b);
          aVal = Object.keys(aCounts).length;
          bVal = Object.keys(bCounts).length;
        } else if (sortConfig.key === "status") {
          const aReceived = opdSupports.filter((s) => s.opdName === a.opdName);
          const bReceived = opdSupports.filter((s) => s.opdName === b.opdName);
          const aCounts = aggregateSupports(a);
          const bCounts = aggregateSupports(b);
          
          const aStatus = Object.keys(aCounts).every((s) => aCounts[s] === 0) ? "Completed" : aReceived.length > 0 ? "In Progress" : "Pending";
          const bStatus = Object.keys(bCounts).every((s) => bCounts[s] === 0) ? "Completed" : bReceived.length > 0 ? "In Progress" : "Pending";
          
          aVal = aStatus;
          bVal = bStatus;
        }
        
        if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
        return 0;
      });
    }
    return sorted;
  }, [filteredOpds, sortConfig, opdSupports]);

  const paginatedOpds = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedOpds.slice(start, start + itemsPerPage);
  }, [sortedOpds, currentPage]);

  const totalPages = Math.ceil(sortedOpds.length / itemsPerPage);

  const requestSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") direction = "desc";
    setSortConfig({ key, direction });
  };

  // Collect unique OPD names and supports for filter dropdowns
  const allOpdNames = [...new Set(opds.map((o) => o.opdName))];
  const allSupports = SUPPORT_CATEGORIES;
  const allOrganizations = [
    ...new Set(
      opdSupports
        .map((s) => s.organization || "Association")
        .filter((org) => org && org.trim() !== "")
    ),
  ];

  // Provided supports list for second tab
  const providedSupportsFiltered = useMemo(() => {
    const search = filters.search.toLowerCase();
    const supportFilter = filters.support;
    const opdFilter = filters.opd;
    const orgFilter = filters.organization;

    return opdSupports.filter((s) => {
      const matchesSupport = supportFilter ? s.supportType === supportFilter : true;
      const matchesOpd = opdFilter ? s.opdName === opdFilter : true;
      const orgLabel = s.organization || "Association";
      const matchesOrg = orgFilter ? orgLabel === orgFilter : true;
      const matchesSearch = search
        ? (s.opdName || '').toLowerCase().includes(search) ||
          (s.supportType || '').toLowerCase().includes(search)
        : true;
      return matchesSupport && matchesOpd && matchesOrg && matchesSearch;
    });
  }, [opdSupports, filters.search, filters.support, filters.opd, filters.organization]);

  const resetProvidedFilters = () => {
    setFilters((prev) => ({
      ...prev,
      search: "",
      opd: "",
      support: "",
      organization: "",
    }));
  };

  const exportCSV = () => {
    const headers = ["OPD Name", "Support Type", "Amount Needed", "Amount Remaining", "Status"];
    const rows = [];
    
    sortedOpds.forEach((opd) => {
      const counts = aggregateSupports(opd);
      const receivedSupportsForOpd = opdSupports.filter((s) => s.opdName === opd.opdName);
      const status = Object.keys(counts).every((s) => counts[s] === 0)
        ? "Completed"
        : receivedSupportsForOpd.length > 0
        ? "In Progress"
        : "Pending";

      Object.entries(counts).forEach(([supportType, remaining]) => {
        const totalNeeded = opd.members.reduce((sum, m) => {
          return sum + m.supportNeeded.filter(s => s === supportType).length;
        }, 0);
        
        rows.push([
          opd.opdName,
          supportType,
          totalNeeded,
          remaining,
          status
        ]);
      });
    });

    let csvContent = "data:text/csv;charset=utf-8," 
      + [headers, ...rows].map(e => e.join(",")).join("\n");

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `opd-support-tracking-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="bg-gray-100 min-h-screen p-4 md:p-8">
      {/* Header */}
      <header className="mb-8">
        <h1 className="text-4xl font-bold text-gray-800">በኦፒዲ ድጋፍ ክትትል/Per OPD Support Tracking</h1>
        <p className="text-gray-500 mt-1">በመላው OPDs የድጋፍ ስርጭትን ይከታተሉ እና ያስተዳድሩ።/Track and manage support distribution across OPDs.</p>
      </header>
      {/* Tabs */}
      <div className="flex gap-3 mb-4">
        <button
          type="button"
          className={`px-4 py-2 rounded-lg font-medium transition-all ${activeTab === 'tracking' ? 'bg-teal-600 text-white shadow' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          onClick={() => {
            if (activeTab === 'provided') {
              resetProvidedFilters();
            }
            setActiveTab('tracking');
          }}
        >
          ክትትል/Tracking
        </button>
        <button
          type="button"
          className={`px-4 py-2 rounded-lg font-medium transition-all ${activeTab === 'provided' ? 'bg-teal-600 text-white shadow' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
          onClick={() => setActiveTab('provided')}
        >
          የተሰጡ ድጋፎች/Provided Supports
        </button>
      </div>

      {/* Control Panel - Tracking Tab */}
      {activeTab === 'tracking' && (
      <div className="bg-white p-6 rounded-xl shadow-md mb-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          {/* Search Input */}
          <div className="relative lg:col-span-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="search"
              placeholder="Search OPDs..."
              value={filters.search}
              onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 transition"
            />
          </div>

          {/* Filters */}
          <select
            name="opd"
            value={filters.opd}
            onChange={onFilterChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 transition bg-white"
          >
            <option value="">All OPDs</option>
            {allOpdNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>

          <select
            name="support"
            value={filters.support}
            onChange={onFilterChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 transition bg-white"
          >
            <option value="">All Supports</option>
            {allSupports.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>

          <select
            name="status"
            value={filters.status}
            onChange={onFilterChange}
            className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 transition bg-white"
          >
            <option value="">ሁሉም ሁኔታዎች/All Status</option>
            <option value="Pending">በመጠባበቅ ላይ/Pending</option>
            <option value="In Progress">በሂደት ላይ ያለ/In Progress</option>
            <option value="Completed">የተጠናቀቀ/Completed</option>
          </select>

          {/* Export Button */}
          <div className="flex items-center justify-end lg:col-span-5">
            <button
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white font-semibold rounded-lg hover:bg-teal-700 transition-colors shadow-sm"
              onClick={exportCSV}
            >
              <Download className="w-5 h-5" />
              Export CSV
            </button>
          </div>
        </div>
      </div>
      )}

      {/* Support Tracking Table - Tracking Tab */}
      {activeTab === 'tracking' && (
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        {loading ? (
          <p className="text-center text-gray-500 py-16">Loading support data...</p>
        ) : sortedOpds.length === 0 ? (
          <p className="text-center text-gray-500 py-16">No OPDs found matching your criteria.</p>
        ) : (
          <>
            {/* Table for Medium screens and up */}
            <div className="overflow-x-auto hidden md:block">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    {[
                      { key: 'opdName', label: 'OPD Name' },
                      { key: 'supportTypes', label: 'Support Types' },
                      { key: 'amounts', label: 'Amount' },
                      { key: 'receivedSupports', label: 'Received Supports' },
                      { key: 'actions', label: 'Actions' },
                      { key: 'status', label: 'Status' },
                    ].map(col => (
                      <th 
                        key={col.key} 
                        onClick={col.key !== 'supportTypes' && col.key !== 'amounts' && col.key !== 'receivedSupports' && col.key !== 'actions' ? () => requestSort(col.key) : undefined}
                        className={`py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider ${
                          col.key !== 'supportTypes' && col.key !== 'amounts' && col.key !== 'receivedSupports' && col.key !== 'actions' 
                            ? 'cursor-pointer hover:bg-gray-100' 
                            : ''
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          {col.label}
                          {(col.key === 'opdName' || col.key === 'status') && sortConfig.key === col.key ? (
                            sortConfig.direction === 'asc' ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                          ) : (col.key === 'opdName' || col.key === 'status') ? <ChevronsUpDown className="w-4 h-4 text-gray-400" /> : null}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {paginatedOpds.map((opd) => {
                    const counts = aggregateSupports(opd);
                    const receivedSupportsForOpd = opdSupports.filter(
                      (s) =>
                        s.opdName === opd.opdName &&
                        (s.organization === "Association" || !s.organization)
                    );

                    const allSupportsForOpd = Object.keys(counts);
                    const status = allSupportsForOpd.every((s) => counts[s] === 0)
                      ? "Completed"
                      : receivedSupportsForOpd.length > 0
                      ? "In Progress"
                      : "Pending";

                    return (
                      <tr key={opd.opdName} className="hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4 text-gray-700 font-medium">{opd.opdName}</td>
                        <td className="py-3 px-4 text-gray-600">
                          <div className="space-y-1">
                            {Object.keys(counts).map((s) => (
                              <div key={s} className="text-sm">{s}</div>
                            ))}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          <div className="space-y-1">
                            {Object.entries(counts).map(([s, count]) => (
                              <div key={s} className="text-sm font-medium">{count}</div>
                            ))}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="space-y-1">
                            {receivedSupportsForOpd.map((s) => (
                              <div
                                key={s.id}
                                className="flex justify-between items-center bg-blue-100 p-2 rounded-lg text-sm"
                              >
                                <span className="text-blue-800">
                                  {s.supportType}
                                  {s.notes ? `: ${s.notes}` : ''}
                                  {` (${s.amount}) - ${s.dateGiven}`}
                                </span>
                                <button
                                  onClick={() => handleRemoveSupport(s.id)}
                                  className="text-red-500 hover:text-red-700 ml-2"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="space-y-2">
                            <div className="flex gap-2">
                              <select
                                value={selectedSupport[opd.opdName] || ""}
                                onChange={(e) =>
                                  setSelectedSupport((prev) => ({
                                    ...prev,
                                    [opd.opdName]: e.target.value,
                                  }))
                                }
                                className="border border-gray-300 rounded-lg px-2 py-1 text-sm flex-1 focus:outline-none focus:ring-2 focus:ring-teal-500"
                              >
                                <option value="">Select Support Type</option>
                                {SUPPORT_CATEGORIES.map((cat) => (
                                  <option key={cat} value={cat}>
                                    {cat}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="flex flex-col gap-2">
                              <input
                                type="text"
                                value={supportDescription[opd.opdName] || ""}
                                onChange={(e) =>
                                  setSupportDescription((prev) => ({
                                    ...prev,
                                    [opd.opdName]: e.target.value,
                                  }))
                                }
                                placeholder="Description"
                                className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                              />
                              <div className="flex gap-2">
                                <input
                                  type="number"
                                  min="1"
                                  value={supportAmount[opd.opdName] || ""}
                                  onChange={(e) =>
                                    setSupportAmount((prev) => ({
                                      ...prev,
                                      [opd.opdName]: e.target.value,
                                    }))
                                  }
                                  className="border border-gray-300 rounded-lg px-2 py-1 w-16 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                  placeholder="Amt"
                                />
                                <input
                                  type="date"
                                  value={supportDate[opd.opdName] || ""}
                                  onChange={(e) =>
                                    setSupportDate((prev) => ({
                                      ...prev,
                                      [opd.opdName]: e.target.value,
                                    }))
                                  }
                                  className="border border-gray-300 rounded-lg px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500"
                                />
                              </div>
                            </div>
                            <button
                              onClick={() => handleAddSupport(opd.opdName)}
                              className="flex items-center gap-1 bg-teal-600 text-white px-3 py-1 rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium w-full justify-center"
                            >
                              <Plus className="w-4 h-4" />
                              Add
                            </button>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <span
                              className={`w-3 h-3 rounded-full ${statusColor(status)}`}
                            ></span>
                            <span className="text-sm font-medium">{status}</span>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Cards for Small screens */}
            <div className="block md:hidden">
              <div className="divide-y divide-gray-200">
                {paginatedOpds.map((opd) => {
                  const counts = aggregateSupports(opd);
                  const receivedSupportsForOpd = opdSupports.filter(
                    (s) =>
                      s.opdName === opd.opdName &&
                      (s.organization === "Association" || !s.organization)
                  );
                  const allSupportsForOpd = Object.keys(counts);
                  const status = allSupportsForOpd.every((s) => counts[s] === 0)
                    ? "Completed"
                    : receivedSupportsForOpd.length > 0
                    ? "In Progress"
                    : "Pending";

                  return (
                    <div key={opd.opdName} className="p-4">
                      <div className="space-y-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-bold text-gray-800">{opd.opdName}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`w-2 h-2 rounded-full ${statusColor(status)}`}></span>
                              <span className="text-sm text-gray-600">{status}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <p className="font-medium text-gray-700 mb-1">Support Types:</p>
                            {Object.keys(counts).map((s) => (
                              <p key={s} className="text-gray-600">{s}</p>
                            ))}
                          </div>
                          <div>
                            <p className="font-medium text-gray-700 mb-1">Remaining:</p>
                            {Object.entries(counts).map(([s, count]) => (
                              <p key={s} className="text-gray-600 font-medium">{count}</p>
                            ))}
                          </div>
                        </div>

                        {receivedSupportsForOpd.length > 0 && (
                          <div>
                            <p className="font-medium text-gray-700 mb-2">Received Supports:</p>
                            <div className="space-y-1">
                              {receivedSupportsForOpd.map((s) => (
                                <div key={s.id} className="bg-blue-100 p-2 rounded text-sm">
                                  <span className="text-blue-800">
                                    {s.supportType}
                                    {s.notes ? `: ${s.notes}` : ''}
                                    {` (${s.amount}) - ${s.dateGiven}`}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Pagination */}
            <div className="flex flex-col md:flex-row justify-between items-center p-4 border-t border-gray-200 gap-4">
              <span className="text-sm text-gray-600">
                Showing {paginatedOpds.length} of {sortedOpds.length} OPDs
              </span>
              <div className="flex items-center gap-2">
                <button
                  className="px-3 py-1 border rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                >
                  Prev
                </button>
                <span className="text-sm text-gray-700">
                  Page {currentPage} of {totalPages}
                </span>
                <button
                  className="px-3 py-1 border rounded-lg bg-white text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition"
                  onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </div>
      )}

      {/* Provided Supports Tab */}
      {activeTab === 'provided' && (
        <div className="bg-white rounded-xl shadow-md overflow-hidden">
          {loading ? (
            <p className="text-center text-gray-500 py-16">Loading support data...</p>
          ) : (
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Provided Supports</h3>
                <span className="px-3 py-1 rounded-full bg-teal-50 text-teal-700 font-semibold text-sm">
                  {providedSupportsFiltered.length} result{providedSupportsFiltered.length !== 1 ? 's' : ''}
                </span>
              </div>

              {/* Filters inside Provided Supports */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="search"
                    placeholder="Search by OPD or support..."
                    value={filters.search}
                    onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 transition"
                  />
                </div>
                <select
                  name="opd"
                  value={filters.opd}
                  onChange={onFilterChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 transition bg-white"
                >
                  <option value="">All OPDs</option>
                  {allOpdNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
                <select
                  name="support"
                  value={filters.support}
                  onChange={onFilterChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 transition bg-white"
                >
                  <option value="">All Supports</option>
                  {allSupports.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                <select
                  name="organization"
                  value={filters.organization}
                  onChange={onFilterChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500 transition bg-white"
                >
                  <option value="">All Organizations</option>
                  {allOrganizations.map((org) => (
                    <option key={org} value={org}>
                      {org}
                    </option>
                  ))}
                </select>
              </div>

              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider">#</th>
                      <th className="py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider">OPD Name</th>
                      <th className="py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider">Support Type</th>
                      <th className="py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider">Organization</th>
                      <th className="py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider">Amount</th>
                      <th className="py-3 px-4 text-left font-semibold text-gray-600 uppercase tracking-wider">Date Given</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {providedSupportsFiltered.map((s, index) => (
                      <tr key={s.id} className="hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4 text-gray-700">{index + 1}</td>
                        <td className="py-3 px-4 text-gray-700">{s.opdName}</td>
                        <td className="py-3 px-4 text-gray-700">
                          {s.supportType}
                          {s.notes ? `: ${s.notes}` : ''}
                        </td>
                        <td className="py-3 px-4 text-gray-700">{s.organization || "Association"}</td>
                        <td className="py-3 px-4 text-gray-700">{s.amount}</td>
                        <td className="py-3 px-4 text-gray-700">{s.dateGiven}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
